import { useState, useEffect } from "react";
import { Close } from "components/assets/Icons";
import styles from "./CallDetailModal.module.scss";
import quoteService from "services/quote.service";

const CallDetailModal = ({
  callId,
  onClose
}) => {
  const [callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (callId) {
      fetchCallDetails();
    }
  }, [callId]);

  const fetchCallDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await quoteService.getCallDetail(callId);
      setCallData(data);
    } catch (error) {
      console.error('Error fetching call details:', error);
      setError('Failed to load call details');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <>
        <div className={styles.header}>
          <h2>Call Details</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Close />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.loading}>Loading call details...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className={styles.header}>
          <h2>Call Details</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Close />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.error}>{error}</div>
        </div>
      </>
    );
  }

  if (!callData) {
    return (
      <>
        <div className={styles.header}>
          <h2>Call Details</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Close />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.noData}>No call data available</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <h2>Call Details</h2>
        <button className={styles.closeButton} onClick={onClose}>
          <Close />
        </button>
      </div>
      <div className={styles.content}>
        <div className={styles.form}>
          <div className={styles.section}>
            <h3>Call Information</h3>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Status:</label>
                <span className={`${styles.status} ${styles[callData.call_status]}`}>
                  {callData.call_status}
                </span>
              </div>
              <div className={styles.formGroup}>
                <label>Duration:</label>
                <span>{formatDuration(callData.duration_ms)}</span>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Successful:</label>
                <span className={callData.call_successful === true ? styles.success :
                              callData.call_successful === false ? styles.failed : styles.pending}>
                  {callData.call_successful === null ? 'Pending' :
                   callData.call_successful ? 'Yes' : 'No'}
                </span>
              </div>
              <div className={styles.formGroup}>
                <label>Phone Number:</label>
                <span>{callData.to_number}</span>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Created:</label>
                <span>{formatDate(callData.created_at)}</span>
              </div>
              <div className={styles.formGroup}>
                <label>Updated:</label>
                <span>{formatDate(callData.updated_at)}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>RFQ & Truck Information</h3>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>RFQ Order Number:</label>
                <span>{callData.rfq_order_number}</span>
              </div>
              <div className={styles.formGroup}>
                <label>Customer:</label>
                <span>{callData.rfq_customer}</span>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Truck Unit:</label>
                <span>{callData.truck_unit}</span>
              </div>
              <div className={styles.formGroup}>
                <label>Truck Type:</label>
                <span>{callData.truck_type}</span>
              </div>
            </div>
          </div>

          {callData.disconnection_reason && (
            <div className={styles.section}>
              <h3>Disconnection Information</h3>
              <div className={styles.formGroup}>
                <label>Reason:</label>
                <span>{callData.disconnection_reason_display || callData.disconnection_reason}</span>
              </div>
            </div>
          )}

          {callData.user_sentiment && (
            <div className={styles.section}>
              <h3>Call Results</h3>
              <div className={styles.formGroup}>
                <label>User Sentiment:</label>
                <span className={`${styles.sentiment} ${styles[callData.user_sentiment?.toLowerCase()]}`}>
                  {callData.user_sentiment}
                </span>
              </div>
            </div>
          )}

          {callData.dynamic_variables && Object.keys(callData.dynamic_variables).length > 0 && (
            <div className={styles.section}>
              <h3>Call Variables</h3>
              <div className={styles.variablesContainer}>
                {Object.entries(callData.dynamic_variables).map(([key, value], index, arr) => {
                  if (index % 2 === 0) {
                    return (
                      <div key={key} className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                          <span>{value}</span>
                        </div>
                        {arr[index + 1] && (
                          <div className={styles.formGroup}>
                            <label>{arr[index + 1][0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                            <span>{arr[index + 1][1]}</span>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {callData.response_variables && Object.keys(callData.response_variables).length > 0 && (
            <div className={styles.section}>
              <h3>Response Variables</h3>
              <div className={styles.variablesContainer}>
                {Object.entries(callData.response_variables).map(([key, value], index, arr) => {
                  if (index % 2 === 0) {
                    return (
                      <div key={key} className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                          <span>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>
                        </div>
                        {arr[index + 1] && (
                          <div className={styles.formGroup}>
                            <label>{arr[index + 1][0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                            <span>{typeof arr[index + 1][1] === 'boolean' ? (arr[index + 1][1] ? 'Yes' : 'No') : arr[index + 1][1]}</span>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {(callData.transcript || callData.summary || callData.recording_url) && (
            <div className={`${styles.section} ${styles.fullWidth}`}>
              {callData.summary && (
                <>
                  <h3>Summary</h3>
                  <div className={styles.summary}>
                    {callData.summary}
                  </div>
                </>
              )}

              {callData.recording_url && (
                <>
                  <h3>Recording</h3>
                  <div className={styles.recording}>
                    <audio controls>
                      <source src={callData.recording_url} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </>
              )}

              {callData.transcript && (
                <>
                  <h3>Transcript</h3>
                  <div className={styles.transcript}>
                    {callData.transcript}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <button
          className={styles.closeBtn}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </>
  );
};

export default CallDetailModal;